'use client';

import React from 'react';
import {
  Button,
  Input,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@heroui/react';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router';
import { parseApiError } from '../../utils/errorParser';
import RightImage from '../sign-in/imagecreator.jpg';
import { AlertBlock, ResponsiveModal, Typography } from '../../components';
import { LogoCreatorAuthIc } from '../../components/icons';
import { useTranslations } from '../../contexts/translation.context';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

const getForgotPasswordSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('auth.validation.emailInvalid'))
      .required(t('auth.validation.emailRequired')),
  });

export default function ForgotPassword() {
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = React.useState<string>('');

  const { resetPasswordClient } = useAuth();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setIsLoading(true);
      setError(null);

      await resetPasswordClient(values);

      setSubmittedEmail(values.email);
      onOpen(); // Показываем модальное окно
    } catch (error: any) {
      console.error('Password reset request failed:', error);
      setError(parseApiError(error, t('auth.errors.forgotPasswordFailed')));
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-white px-4 md:px-6 md:gap-12 pt-[5.25rem] pb-4 md:py-0">
      <div className="flex flex-col justify-end md:justify-normal fixed top-0 left-0 w-full z-50 bg-white md:bg-transparent border-b border-solid border-neutral-12 px-4 py-6 md:px-12 md:pt-12 md:border-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] md:w-[7.5rem] md:h-9 shrink-0">
            <LogoCreatorAuthIc className="size-full" />
          </div>
          <div className="md:hidden">
            <LanguageSwitcher variant="ghost" />
          </div>
        </div>
      </div>

      <div className="flex w-full md:min-h-[calc(100vh-48px)] flex-col justify-center md:flex-row md:items-stretch">
        <div className="flex w-full items-center justify-center bg-background py-6 md:px-6 md:py-12 md:flex-1">
          <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-6">
            <div className="w-full text-center flex flex-col gap-1 md:gap-4">
              <Typography variant="h3">{t('auth.forgotPasswordTitle')}</Typography>
              <Typography variant="body1" className="px-4 text-auth-muted">
                {t('auth.forgotPasswordDescription')}
              </Typography>
            </div>

            {error && (
              <AlertBlock
                type="rounded"
                data={[{ severity: 'error', description: error }]}
                className="mb-4"
              />
            )}

            <Formik
              initialValues={{
                email: '',
              }}
              validationSchema={getForgotPasswordSchema(t)}
              onSubmit={handleSubmit}
            >
              {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
                <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit} noValidate>
                  <div className="flex flex-col w-full">
                    <Input
                      label="E-mail"
                      name="email"
                      placeholder={t('auth.enterEmail')}
                      type="email"
                      variant="bordered"
                      value={values.email}
                      onChange={(e) => setFieldValue('email', e.target.value)}
                      labelPlacement="inside"
                      className="w-full custom-required-label"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <Button
                    className="w-full h-12 mt-4 text-base font-semibold"
                    color="primary"
                    type="submit"
                    isLoading={isLoading || isSubmitting}
                    disabled={isLoading || isSubmitting}
                  >
                    {isLoading || isSubmitting ? t('auth.sending') : t('auth.continue')}
                  </Button>
                  <div
                    onClick={() => navigate('/client/sign-in', { state: { skipSelect: true } })}
                    className="w-full cursor-pointer h-12 bg-auth-surface rounded-2 hover:bg-auth-surface-hover transition-colors user-type-card flex items-center justify-center"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-auth-ink">
                        {t('auth.backToLogin')}
                      </span>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>

        <div className="hidden w-full items-stretch gap-12 md:flex md:flex-1">
          <div className="shrink-0">
            <LanguageSwitcher variant="ghost" />
          </div>
          <div
            className="hidden h-full flex-1 overflow-hidden rounded-8 md:block"
            style={{
              backgroundImage: `url(${RightImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
      </div>
      {/* Success Modal */}
      <ResponsiveModal isOpen={isOpen} onClose={onClose}>
        <ModalContent className="md:w-[25rem]">
          <ModalHeader className="flex flex-col gap-1">
            <h3 className="text-lg font-medium">{t('auth.emailSent')}</h3>
          </ModalHeader>
          <ModalBody className="pt-3 px-4 md:px-6 pb-6">
            <p className="text-sm leading-[1.125rem] text-auth-ink">
              {t('auth.emailSentDescription')}&nbsp;
              <span className="text-sm leading-5 font-medium">{submittedEmail}</span>
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={onClose}>
              {t('auth.continue')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
}
